import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isBalconyOrPorchPermitGR6_1,
	isResidentialLandPermitGR6_2,
	isResidentialLandPermitGR6_4
} from '../__index.utils'

export const STEP3FIELDS = {
	mainUseOfExistingBuilding: 'mainUseOfExistingBuilding',
	mainUseOfExistingBuildingForParking: 'mainUseOfExistingBuildingForParking',
	isResidentialOrOther: 'isResidentialOrOther',
	structureType: 'structureType',
	existingMainBuildingIsShared: 'existingMainBuildingIsShared',
	useOfBuildingOrLand: 'useOfBuildingOrLand'
} as const

export type Step3Fields = keyof typeof STEP3FIELDS

/**
 * useStep3State - Custom hook to manage the state for step 3 of the GR6 Land Development Work Permit form.
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 3.
 */

export function useStep3State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR6_1 = isBalconyOrPorchPermitGR6_1(answers?.whatToDo)
	const isGR6_2 = isResidentialLandPermitGR6_2(answers?.whatToDo)
	const isGR6_4 = isResidentialLandPermitGR6_4(answers?.whatToDo)

	return {
		//  "Quel est l'usage du bâtiment principal existant?"
		mainUseOfExistingBuilding: {
			name: 'mainUseOfExistingBuilding',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_mainUseOfExistingBuilding,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_mainUseOfExistingBuilding',
			value: answers?.mainUseOfExistingBuilding?.toString() || '',
			required: isGR6_1,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		},
		// Les travaux s'appliquent-ils à un usage résidentiel ou autre que résidentiel (industriel, commercial ou institutionnel)?
		isResidentialOrOther: {
			name: 'isResidentialOrOther',
			label:
				pageAssets?.request_form_landDevWorkPermit_isResidentialOrOther_updated,
			labelKey: 'request_form_landDevWorkPermit_isResidentialOrOther',
			value: answers?.isResidentialOrOther?.toString() || '',
			required:
				(isGR6_1 &&
					answers?.mainUseOfExistingBuilding?.toString() ==
						REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.fourthOption) ||
				(isGR6_2 &&
					answers?.useOfBuildingOrLand?.toString() ==
						REQUEST_CONFIGS_KEYS.useOfBuildingOrLand
							.agriculturalIndustrialInstitutionalCommercialOrMixed) ||
				(isGR6_4 &&
					answers?.useOfBuildingOrLand !==
						REQUEST_CONFIGS_KEYS.useOfBuildingBoat.emptyLot),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		},
		// Quel est le type de structure?
		structureType: {
			name: 'structureType',
			label:
				pageAssets?.request_form_buildingDemolitionOrMovingPermit_structureType,
			labelKey: 'request_form_buildingDemolitionOrMovingPermit_structureType',
			value: answers?.structureType?.toString() || '',
			required: isGR6_1,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		},
		// Le bâtiment principal existant est-il ou fait-il partie d'une copropriété?
		existingMainBuildingIsShared: {
			name: 'existingMainBuildingIsShared',
			label:
				pageAssets?.request_form_buildingDemolitionOrMovingPermit_isTheNewBuildingPartOfAProperty,
			labelKey:
				'request_form_buildingDemolitionOrMovingPermit_isTheNewBuildingPartOfAProperty',
			value: answers?.existingMainBuildingIsShared?.toString() || '',
			required:
				answers?.useOfBuildingOrLand !==
				REQUEST_CONFIGS_KEYS.useOfBuildingBoat.emptyLot,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		},
		// * Pour le groupe GR6.2
		// Quel est l'usage du bâtiment ou du terrain?
		useOfBuildingOrLand: {
			name: 'useOfBuildingOrLand',
			label: pageAssets?.request_form_landDevWorkPermit_useOfBuildingOrLand,
			labelKey: 'request_form_landDevWorkPermit_useOfBuildingOrLand',
			value: answers?.useOfBuildingOrLand?.toString() || '',
			required: isGR6_2,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		}
	}
}
